export const TARIFA_PLANA_BASE = 80;

export const MEI_RATE_BY_YEAR: Record<number, number> = {
  2025: 0.008,
  2026: 0.009
};

export const RETA_MAX_BASE_BY_YEAR: Record<number, number> = {
  2025: 4909.5,
  2026: 5101.2
};

export const RETA_MIN_BASE_RULE_BY_YEAR: Record<number, string> = {
  2026: "SMI + 1/6"
};

export const RETA_RATES_BY_YEAR: Record<
  number,
  {
    contingenciasComunes: number;
    contingenciasProfesionales: number;
    ceseActividad: number;
    formacion: number;
    mei: number;
  }
> = {
  2025: {
    contingenciasComunes: 0.283,
    contingenciasProfesionales: 0.013,
    ceseActividad: 0.009,
    formacion: 0.001,
    mei: 0.008
  },
  2026: {
    contingenciasComunes: 0.283,
    contingenciasProfesionales: 0.013,
    ceseActividad: 0.009,
    formacion: 0.001,
    mei: 0.009
  }
};

export type RetaTramo = {
  min: number;
  max: number;
  baseMin: number;
};

export const RETA_TRAMOS_BY_YEAR: Record<number, RetaTramo[]> = {
  2025: [
    { min: 0, max: 670, baseMin: 653.59 },
    { min: 670.01, max: 900, baseMin: 718.95 },
    { min: 900.01, max: 1166.7, baseMin: 849.67 },
    { min: 1166.7, max: 1300, baseMin: 950.98 },
    { min: 1300.01, max: 1500, baseMin: 960.78 },
    { min: 1500.01, max: 1700, baseMin: 960.78 },
    { min: 1700.01, max: 1850, baseMin: 1143.79 },
    { min: 1850.01, max: 2030, baseMin: 1209.15 },
    { min: 2030.01, max: 2330, baseMin: 1274.51 },
    { min: 2330.01, max: 2760, baseMin: 1356.21 },
    { min: 2760.01, max: 3190, baseMin: 1437.91 },
    { min: 3190.01, max: 3620, baseMin: 1519.61 },
    { min: 3620.01, max: 4050, baseMin: 1601.31 },
    { min: 4050.01, max: 6000, baseMin: 1732.03 },
    { min: 6000.01, max: Infinity, baseMin: 1928.1 }
  ]
};

const roundTo = (value: number, decimals: number) => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

const latestMeiYear = Math.max(...Object.keys(MEI_RATE_BY_YEAR).map((year) => Number(year)));
const latestRetaYear = Math.max(...Object.keys(RETA_RATES_BY_YEAR).map((year) => Number(year)));
const latestTramosYear = Math.max(...Object.keys(RETA_TRAMOS_BY_YEAR).map((year) => Number(year)));

export const getMeiRate = (year: number) => {
  if (MEI_RATE_BY_YEAR[year] !== undefined) return MEI_RATE_BY_YEAR[year];
  return MEI_RATE_BY_YEAR[latestMeiYear] ?? 0;
};

export const getRetaRates = (year: number) => {
  if (RETA_RATES_BY_YEAR[year]) return RETA_RATES_BY_YEAR[year];
  return RETA_RATES_BY_YEAR[latestRetaYear];
};

export const getRetaTramos = (year: number) => {
  if (RETA_TRAMOS_BY_YEAR[year]) return RETA_TRAMOS_BY_YEAR[year];
  return RETA_TRAMOS_BY_YEAR[latestTramosYear];
};

export const getRetaTotalRate = (year: number) => {
  const rates = getRetaRates(year);
  return (
    rates.contingenciasComunes +
    rates.contingenciasProfesionales +
    rates.ceseActividad +
    rates.formacion +
    rates.mei
  );
};

export const getTarifaPlanaDetails = (year: number) => {
  const meiRate = getMeiRate(year);
  const meiAmount = roundTo(TARIFA_PLANA_BASE * 12 * meiRate, 2);
  const totalMonthly = roundTo(TARIFA_PLANA_BASE + meiAmount, 2);
  const totalAnnual = roundTo(totalMonthly * 12, 2);

  return {
    baseMonthly: TARIFA_PLANA_BASE,
    meiRate,
    meiAmount,
    totalMonthly,
    totalAnnual
  };
};

export const suggestRetaAnnualFromIncome = (year: number, rendimientoAnual: number) => {
  const mensual = rendimientoAnual / 12;
  const tramos = getRetaTramos(year);
  const tramo = tramos.find((t) => mensual >= t.min && mensual <= t.max) ?? tramos[tramos.length - 1];
  const totalRate = getRetaTotalRate(year);
  const mensualCuota = roundTo(tramo.baseMin * totalRate, 2);
  return roundTo(mensualCuota * 12, 2);
};
